import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Calendar, Search } from "lucide-react";
import { formatDate } from "@/lib/date-utils";

export interface TableColumn {
  key: string;
  header: string;
  render: (item: any) => React.ReactNode;
}

interface FilterTab {
  label: string;
  value: string;
}

interface ListPageTemplateProps {
  title: string;
  items: any[];
  columns: TableColumn[];
  isLoading: boolean;
  onCreateNew: () => void;
  createButtonText?: string;
  emptyMessage?: string;
  children?: React.ReactNode; // For additional dialogs/modals
  // Filter tabs support
  filterTabs?: FilterTab[];
  activeTab?: string;
  onTabChange?: (value: string) => void;
  // Search support
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  // Key extraction for table rows
  getKey?: (item: any, index: number) => string | number;
}

export const ListPageTemplate = React.memo(function ListPageTemplate({
  title,
  items,
  columns,
  isLoading,
  onCreateNew,
  createButtonText = i18n.t("components.listPageTemplate.new"),
  emptyMessage = i18n.t("components.listPageTemplate.noItemsFoundCreate"),
  children,
  // Filter tabs props
  filterTabs,
  activeTab,
  onTabChange,
  // Search props
  searchQuery,
  onSearchChange,
  searchPlaceholder = i18n.t("components.listPageTemplate.search"),
  // Key extraction prop
  getKey,
}: ListPageTemplateProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      {/* Header with title and create button */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{title}</h1>
        <Button onClick={onCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          {createButtonText}
        </Button>
      </div>

      {/* Filter Tabs - full width */}
      {filterTabs && filterTabs.length > 0 && (
        <div className="w-full">
          <Tabs
            value={activeTab}
            onValueChange={onTabChange}
            className="w-full"
          >
            <TabsList className="w-full justify-evenly">
              {filterTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex-1"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Search Input - full width */}
      {searchQuery !== undefined && onSearchChange && (
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 w-full"
          />
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div>{t("components.listPageTemplate.loading")}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key}>{column.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items && items.length > 0 ? (
              items.map((item, index) => (
                <TableRow
                  key={getKey ? getKey(item, index) : item.uid || index}
                >
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      {column.render(item)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-6"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {children}
    </div>
  );
});

// Helper components for common cell types
export const StatusBadge = React.memo(
  ({
    status,
    statusColors,
    formatStatus,
  }: {
    status: string;
    statusColors: Record<string, string>;
    formatStatus?: (status: string) => string;
  }) => (
    <Badge
      variant="status"
      className={`${statusColors[status] || "bg-gray-500"} text-white whitespace-nowrap text-xs shrink-0`}
    >
      {formatStatus ? formatStatus(status) : status}
    </Badge>
  ),
);

export const DateCell = React.memo(
  ({ date }: { date: string | Date | null | undefined }) => {
    const { t } = useTranslation();
    if (!date) {
      return (
        <div className="flex items-center">
          <Calendar className="h-4 w-4 mr-2" />
          {t("components.listPageTemplate.noDate")}
        </div>
      );
    }

    return (
      <div className="flex items-center">
        <Calendar className="h-4 w-4 mr-2" />
        {formatDate(date)}
      </div>
    );
  },
);

export const TitleDescriptionCell = React.memo(
  ({ title, description }: { title: string; description?: string }) => (
    <div className="font-medium">
      <div>{title}</div>
      {description && (
        <div className="text-sm text-muted-foreground">{description}</div>
      )}
    </div>
  ),
);
